import { createTheme, Theme } from '@mui/material/styles';

// Barlow font family from SeaCat website for clean, modern look
const barlowFont = '"Barlow", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Enhanced color palette preserving original TremChain colors
export const enhancedColorPalette = {
  primary: {
    main: '#667eea',
    50: '#f0f2ff',
    100: '#e0e5ff',
    200: '#c7cfff',
    300: '#a5b0ff',
    400: '#8f9fff',
    500: '#667eea',
    600: '#5a6fd8',
    700: '#4d5fb5',
    800: '#42519c',
    900: '#374382',
  },
  secondary: {
    main: '#764ba2',
    50: '#f7f3ff',
    100: '#efe6ff',
    200: '#e1d1ff',
    300: '#cdb0ff',
    400: '#b48aff',
    500: '#764ba2',
    600: '#6b4391',
    700: '#5e3b7f',
    800: '#52346d',
    900: '#462c5b',
  },
  gradient: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    primaryDeep: 'linear-gradient(135deg, #5a6fd8 0%, #6b4391 100%)',
    primaryLight: 'linear-gradient(135deg, #8f9fff 0%, #b48aff 100%)',
  },
  background: {
    default: '#fafafa',
    paper: '#ffffff',
    glass: 'rgba(255, 255, 255, 0.1)',
    glassStrong: 'rgba(255, 255, 255, 0.2)',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#6b7280',
    onGradient: '#ffffff',
  },
  elevation: {
    1: '0 2px 8px rgba(0, 0, 0, 0.1)',
    2: '0 4px 16px rgba(0, 0, 0, 0.12)',
    3: '0 8px 32px rgba(0, 0, 0, 0.15)',
  },
};

export const createEnhancedTheme = (isDark: boolean = false): Theme => {
  const baseTheme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        ...enhancedColorPalette.primary,
      },
      secondary: {
        ...enhancedColorPalette.secondary,
      },
      background: {
        default: isDark ? '#121212' : enhancedColorPalette.background.default,
        paper: isDark ? '#1e1e1e' : enhancedColorPalette.background.paper,
      },
      text: {
        primary: isDark ? '#ffffff' : enhancedColorPalette.text.primary,
        secondary: isDark ? '#b3b3b3' : enhancedColorPalette.text.secondary,
      },
    },
    typography: {
      fontFamily: barlowFont,
      h1: {
        fontFamily: barlowFont,
        fontWeight: 700,
        fontSize: '3.5rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: barlowFont,
        fontWeight: 600,
        fontSize: '2.5rem',
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontFamily: barlowFont,
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1.4,
      },
      h4: {
        fontFamily: barlowFont,
        fontWeight: 500,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      h5: {
        fontFamily: barlowFont,
        fontWeight: 500,
        fontSize: '1.25rem',
        lineHeight: 1.5,
      },
      h6: {
        fontFamily: barlowFont,
        fontWeight: 500,
        fontSize: '1.125rem',
        lineHeight: 1.5,
      },
      body1: {
        fontFamily: barlowFont,
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontFamily: barlowFont,
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontFamily: barlowFont,
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
      caption: {
        fontFamily: barlowFont,
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: 1.4,
        letterSpacing: '0.03em',
      },
      overline: {
        fontFamily: barlowFont,
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.4,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@import': [
            'url("https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&display=swap")',
          ],
          body: {
            fontFamily: barlowFont,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: enhancedColorPalette.elevation[1],
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: enhancedColorPalette.elevation[2],
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            padding: '10px 24px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          contained: {
            boxShadow: enhancedColorPalette.elevation[1],
            '&:hover': {
              boxShadow: enhancedColorPalette.elevation[2],
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'all 0.2s ease',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: enhancedColorPalette.primary.main,
                },
              },
            },
          },
        },
      },
    },
  });

  return baseTheme;
};

export default enhancedColorPalette;